import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import { Search, SlidersHorizontal, X, Flame, Tag, User, Heart, Info } from 'lucide-react';
import { useListings, useSearchUsers, useWishlistListings } from '@/hooks/useApi';
import { useAuthStore } from '@/store';
import { Listing } from '@/types';
import { formatPrice } from '@/utils';
import UserAvatar from '@/components/UserAvatar';
import CardTypeFilter, { CardTypeValue } from '@/components/CardTypeFilter';
import WishlistButton from '@/components/WishlistButton';

const CONDITIONS = ['Mint', 'Near Mint', 'Lightly Played', 'Moderately Played', 'Heavily Played'];
const SORT_OPTIONS = [
  { value: 'views',      label: 'Trending'          },
  { value: 'recent',     label: 'Newest First'      },
  { value: 'price_asc',  label: 'Price: Low to High'},
  { value: 'price_desc', label: 'Price: High to Low'},
];

// ─── Wishlist match badge ─────────────────────────────────────
function WishlistMatchBadge({ cardName }: { cardName: string }) {
  return (
    <div className="absolute top-2 right-2 z-10 flex items-center gap-1 bg-blue-600/90 backdrop-blur-sm text-white text-xs font-semibold px-2 py-1 rounded-lg shadow-sm max-w-[90%]">
      <Heart className="h-2.5 w-2.5 fill-white flex-shrink-0" />
      <span className="truncate">
        {cardName ? `Matches: ${cardName}` : 'Matches your wishlist'}
      </span>
    </div>
  );
}

export default function ExplorePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuthStore();

  // Wishlist toggle state lives in the URL
  const wishlistMode = searchParams.get('wishlist') === 'true';

  const [search, setSearch]                       = useState(searchParams.get('q') ?? '');
  const [activeType, setActiveType]               = useState<CardTypeValue>('all');
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [sortBy, setSortBy]                       = useState('views');
  const [showFilters, setShowFilters]             = useState(false);

  useEffect(() => {
    setSearch(searchParams.get('q') ?? '');
  }, [searchParams]);

  // ── Normal listing fetch ─────────────────────────────────────
  const { data: listingsRaw, isLoading: isListingsLoading } = useListings({
    card_type: activeType !== 'all' ? activeType : undefined,
    search:    search || undefined,
    condition: selectedConditions.length === 1 ? selectedConditions[0] : undefined,
  });

  // ── Wishlist-filtered listing fetch ──────────────────────────
  const {
    data: wishlistData,
    isLoading: isWishlistLoading,
  } = useWishlistListings({ enabled: wishlistMode && isAuthenticated });

  // ── Similar cards — same game type, excluding the search term ──
  // Derives game from first result so we don't need an extra field
  const firstGame = (listingsRaw as any)?.[0]?.card?.game;
  const { data: similarRaw } = useListings({
    card_type: firstGame ?? undefined,
    enabled: !wishlistMode && !!search && !!firstGame,
  });
  // Exclude exact matches already shown and cap at 6
  const similarListings = ((similarRaw as any[]) ?? [])
    .filter((l: any) => {
      const name = (l.card?.name ?? l.title ?? '').toLowerCase();
      return !name.includes(search.toLowerCase());
    })
    .slice(0, 6);

  // ── Seller search ─────────────────────────────────────────────
  const { data: userResults = [], isLoading: usersLoading } = useSearchUsers(
    wishlistMode ? '' : search        // don't search sellers in wishlist mode
  );

  // ── Derive displayed listings ─────────────────────────────────
  const isLoading = wishlistMode ? isWishlistLoading : isListingsLoading;

  const normalListings = (listingsRaw as any) as Listing[] | undefined;
  const wishlistListings: (Listing & {
    matched_wishlist_item_id?: string;
    matched_wishlist_card_name?: string;
  })[] = (wishlistData as any)?.listings ?? [];
  const wishlistMeta: { reason?: string } | null = (wishlistData as any)?.meta ?? null;

  const displayedListings = wishlistMode ? wishlistListings : (normalListings ?? []);

  const sorted = wishlistMode
    ? displayedListings           // wishlist results keep server order (most recent)
    : [...(normalListings ?? [])].sort((a, b) => {
        if (sortBy === 'views')      return (b.views_count ?? 0) - (a.views_count ?? 0);
        if (sortBy === 'recent')     return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        if (sortBy === 'price_asc')  return (a.price ?? 0) - (b.price ?? 0);
        if (sortBy === 'price_desc') return (b.price ?? 0) - (a.price ?? 0);
        return 0;
      });

  const activeFilterCount = selectedConditions.length;
  const clearSearch = useCallback(() => setSearch(''), []);

  // ── Wishlist toggle helpers ───────────────────────────────────
  const toggleWishlist = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (wishlistMode) {
        next.delete('wishlist');
      } else {
        next.set('wishlist', 'true');
      }
      return next;
    });
  };

  // ── Wishlist-mode banner content ──────────────────────────────
  const renderWishlistBanner = () => {
    if (!wishlistMode) return null;

    if (!isAuthenticated) {
      return (
        <div className="flex items-center gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 text-blue-800 dark:text-blue-200 rounded-xl px-4 py-3 mb-6 text-sm">
          <Info className="h-4 w-4 flex-shrink-0" />
          <span>
            <Link to="/login" className="font-semibold underline">Sign in</Link>
            {' '}to filter by your wishlist.
          </span>
        </div>
      );
    }

    if (isWishlistLoading) return null;

    if (wishlistMeta?.reason === 'empty_wishlist') {
      return (
        <div className="flex items-center gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 text-blue-800 dark:text-blue-200 rounded-xl px-4 py-3 mb-6 text-sm">
          <Heart className="h-4 w-4 flex-shrink-0" />
          <span>
            Your wishlist is empty.{' '}
            <Link to="/wishlist" className="font-semibold underline">
              Add cards to your wishlist
            </Link>
            {' '}to use this filter.
          </span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 text-blue-800 dark:text-blue-200 rounded-xl px-4 py-3 mb-6 text-sm">
        <Heart className="h-4 w-4 fill-blue-600 flex-shrink-0" />
        <span>
          {wishlistListings.length > 0 ? (
            <>
              Showing listings that match your wishlist
              <span className="font-bold"> · {wishlistListings.length} found</span>
            </>
          ) : (
            <>
              No listings currently match your wishlist.{' '}
              <span className="text-blue-600 font-medium">
                We'll notify you in messages when one is posted.
              </span>
            </>
          )}
        </span>
        <button
          onClick={toggleWishlist}
          className="ml-auto text-blue-500 hover:text-blue-700 dark:text-blue-300 flex-shrink-0"
          title="Clear wishlist filter"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-zinc-900 min-h-screen">
      <SEO
        url="/explore"
        description="Browse and filter trading card listings from collectors worldwide. Find Pokemon, Magic: The Gathering, Yu-Gi-Oh!, and more."
      />
      {/* ── Hero ──────────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-brand-500 to-accent-500 text-white py-12">
        <div className="container-custom">
          <h1 className="text-4xl font-display font-bold mb-2">Explore Cards</h1>
          <p className="text-brand-100 text-lg mb-5">
            Discover the most popular listings from collectors worldwide
          </p>

          <CardTypeFilter
            active={activeType}
            onChange={(t) => { setActiveType(t); if (wishlistMode) toggleWishlist(); }}
            variant="light"
          />

          <div className="relative mt-4 max-w-2xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500 dark:text-zinc-500" />
            <input
              type="text"
              placeholder="Search cards, sets, sellers…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); if (wishlistMode) toggleWishlist(); }}
              className="w-full pl-12 pr-10 py-3 rounded-xl text-gray-900 dark:text-zinc-100 text-base shadow-lg dark:shadow-black/40 focus:outline-none focus:ring-2 focus:ring-accent-400"
            />
            {search && (
              <button
                onClick={clearSearch}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-400 dark:text-zinc-400"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Body ──────────────────────────────────────────────── */}
      <div className="container-custom py-8">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Condition / regular filters — hidden in wishlist mode */}
            {!wishlistMode && (
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border font-medium transition-colors ${
                  showFilters || activeFilterCount > 0
                    ? 'bg-brand-600 text-white border-brand-600'
                    : 'bg-white dark:bg-zinc-900 text-gray-700 dark:text-zinc-300 border-gray-200 dark:border-zinc-800 hover:border-brand-400'
                }`}
              >
                <SlidersHorizontal className="h-4 w-4" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="bg-white dark:bg-zinc-900 text-brand-600 dark:text-brand-400 text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            )}

            {/* Wishlist toggle */}
            <button
              onClick={toggleWishlist}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border font-medium transition-colors ${
                wishlistMode
                  ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                  : 'bg-white dark:bg-zinc-900 text-gray-700 dark:text-zinc-300 border-gray-200 dark:border-zinc-800 hover:border-blue-400 hover:text-blue-600'
              }`}
            >
              <Heart className={`h-4 w-4 ${wishlistMode ? 'fill-white' : ''}`} />
              {wishlistMode ? 'Wishlist matches' : 'Show my wishlist matches'}
            </button>
          </div>

          {/* Sort — hidden in wishlist mode */}
          {!wishlistMode && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 dark:text-zinc-500">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border border-gray-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm bg-white dark:bg-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand-400"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Condition filter panel */}
        {showFilters && !wishlistMode && (
          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 p-6 mb-6">
            <h3 className="font-semibold text-gray-700 dark:text-zinc-300 mb-3">Condition</h3>
            <div className="flex flex-wrap gap-2">
              {CONDITIONS.map((c) => (
                <button
                  key={c}
                  onClick={() =>
                    setSelectedConditions((prev) =>
                      prev.includes(c) ? prev.filter((v) => v !== c) : [...prev, c]
                    )
                  }
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    selectedConditions.includes(c)
                      ? 'bg-accent-500 text-white'
                      : 'bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-zinc-400 hover:bg-accent-50 hover:text-accent-700'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            {activeFilterCount > 0 && (
              <button
                onClick={() => setSelectedConditions([])}
                className="mt-4 text-sm text-red-500 hover:text-red-700 dark:text-red-300 font-medium"
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        {/* ── Wishlist banner ──────────────────────────────────── */}
        {renderWishlistBanner()}

        {/* ── Sellers section (normal mode only, when searching) ── */}
        {!wishlistMode && search.length > 1 && (
          <div className="mb-8">
            <h2 className="text-sm font-semibold text-gray-500 dark:text-zinc-500 uppercase tracking-wide mb-3 flex items-center gap-2">
              <User className="h-4 w-4" /> Sellers
            </h2>
            {usersLoading ? (
              <div className="flex gap-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="skeleton h-16 w-48 rounded-xl" />
                ))}
              </div>
            ) : userResults.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-zinc-500">No sellers found for "{search}"</p>
            ) : (
              <div className="flex flex-wrap gap-3">
                {userResults.map((user: any) => (
                  <Link
                    key={user.id}
                    to={`/profile/${user.id}`}
                    className="flex items-center gap-3 bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 shadow-sm px-4 py-3 hover:border-brand-300 hover:shadow-md transition-all"
                  >
                    <UserAvatar username={user.username} avatarUrl={user.avatar_url} size="sm" />
                    <div>
                      <p className="font-semibold text-sm">{user.username}</p>
                      <p className="text-xs text-gray-500 dark:text-zinc-500">
                        {user.total_sales ?? 0} sales · ★{' '}
                        {user.seller_rating ? Number(user.seller_rating).toFixed(1) : '—'}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Listings section ── */}
        <div>
          {!wishlistMode && search.length > 1 && (
            <h2 className="text-sm font-semibold text-gray-500 dark:text-zinc-500 uppercase tracking-wide mb-3 flex items-center gap-2">
              <Tag className="h-4 w-4" /> Listings
            </h2>
          )}

          {!isLoading && !wishlistMode && (
            <p className="text-sm text-gray-500 dark:text-zinc-500 mb-5">
              {sorted.length === 0
                ? 'No listings found'
                : `${sorted.length} listing${sorted.length !== 1 ? 's' : ''} found${
                    search ? ` for "${search}"` : ''
                  }`}
            </p>
          )}

          {isLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {[...Array(20)].map((_, i) => (
                <div key={i} className="skeleton h-72 rounded-xl" />
              ))}
            </div>
          ) : sorted.length === 0 && !wishlistMode ? (
            <div className="text-center py-24">
              <p className="text-5xl mb-4">🃏</p>
              <h3 className="text-xl font-bold text-gray-700 dark:text-zinc-300 mb-2">
                {search ? `No listings for "${search}"` : 'No listings found'}
              </h3>
              <p className="text-gray-500 dark:text-zinc-500 mb-6">
                {search ? 'Try a different search term' : 'Be the first to list a card!'}
              </p>
              {search ? (
                <button onClick={clearSearch} className="btn-primary">
                  Clear Search
                </button>
              ) : (
                <Link to="/listings/create" className="btn-primary">
                  Create a Listing
                </Link>
              )}
            </div>
          ) : sorted.length === 0 && wishlistMode ? null /* banner already handles empty */ : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {sorted.map((listing: any, index: number) => (
                <div key={listing.id} className="relative group">
                  <Link
                    to={`/listings/${listing.id}`}
                    className="card-hover block bg-white dark:bg-zinc-900 rounded-xl overflow-hidden shadow-sm border border-gray-200 dark:border-zinc-800"
                  >
                  <div className="aspect-[3/4] bg-gradient-to-br from-zinc-800 to-gray-200 relative overflow-hidden">
                    {listing.image_urls?.[0] || listing.card?.image_url ? (
                      <img
                        src={listing.image_urls?.[0] ?? listing.card?.image_url}
                        alt={listing.card?.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-500 dark:text-zinc-500 text-4xl">
                        🃏
                      </div>
                    )}

                    {/* Wishlist match badge */}
                    {wishlistMode && listing.matched_wishlist_item_id && (
                      <WishlistMatchBadge cardName={listing.matched_wishlist_card_name ?? ''} />
                    )}

                    {/* Hot badge (normal mode only) */}
                    {!wishlistMode && index === 0 && sortBy === 'views' && (listing.views_count ?? 0) > 1 && (
                      <div className="absolute top-2 left-2 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-lg flex items-center gap-1">
                        <Flame className="h-3 w-3" /> Hot
                      </div>
                    )}

                    {(listing.views_count ?? 0) > 0 && (
                      <div className="absolute bottom-2 right-2 bg-black/50 backdrop-blur-sm text-white text-xs px-2 py-0.5 rounded-full">
                        {listing.views_count} views
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className="font-semibold text-sm truncate">
                      {listing.card?.name ?? listing.title}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-zinc-500 truncate">
                      {listing.condition} · {listing.card?.game ?? listing.card?.set_name}
                    </p>
                    {listing.card?.rarity && (
                      <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs bg-accent-100 text-accent-700 font-medium">
                        {listing.card.rarity}
                      </span>
                    )}
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-brand-600 dark:text-brand-400 font-bold text-sm">
                        {formatPrice(listing.price)}
                      </p>
                      <span className="flex items-center gap-0.5 text-xs text-gray-500 dark:text-zinc-500">
                        <Tag className="h-3 w-3" />
                        {(listing.listing_type as string) === 'trade' ? 'Trade' : 'Sale'}
                      </span>
                    </div>
                  </div>
                  </Link>

                  {/* Wishlist button — outside Link so it doesn't navigate */}
                  <WishlistButton
                    cardName={listing.card?.name ?? listing.title}
                    cardImageUrl={listing.image_urls?.[0] ?? listing.card?.image_url}
                    setName={listing.card?.set_name}
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Similar cards from the same game */}
          {!wishlistMode && search && similarListings.length > 0 && (
            <div className="mt-10">
              <h3 className="text-base font-bold text-gray-700 dark:text-zinc-300 mb-4">
                Similar {firstGame ?? 'cards'} you might like
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {similarListings.map((listing: any) => (
                  <div key={listing.id} className="relative group">
                    <Link
                      to={`/listings/${listing.id}`}
                      className="block bg-white dark:bg-zinc-900 rounded-xl overflow-hidden shadow-sm border border-gray-200 dark:border-zinc-800 card-hover"
                    >
                      <div className="aspect-[3/4] bg-gradient-to-br from-zinc-800 to-gray-200 relative overflow-hidden">
                        {listing.image_urls?.[0] || listing.card?.image_url ? (
                          <img
                            src={listing.image_urls?.[0] ?? listing.card?.image_url}
                            alt={listing.card?.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-500 dark:text-zinc-500 text-3xl">🃏</div>
                        )}
                      </div>
                      <div className="p-2">
                        <p className="text-xs font-semibold truncate">{listing.card?.name ?? listing.title}</p>
                        <p className="text-xs text-brand-600 dark:text-brand-400 font-bold">{formatPrice(listing.price)}</p>
                      </div>
                    </Link>
                    <WishlistButton
                      cardName={listing.card?.name ?? listing.title}
                      cardImageUrl={listing.image_urls?.[0] ?? listing.card?.image_url}
                      setName={listing.card?.set_name}
                      className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {search && !wishlistMode && (
            <div className="mt-4 text-center">
              <Link
                to={`/forum?search=${encodeURIComponent(search)}`}
                className="text-sm text-gray-500 dark:text-zinc-500 hover:text-brand-600 dark:text-brand-400 transition-colors"
              >
                Discuss "{search}" in the forum →
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
